import type { HealthcareService, Location } from "fhir/r3"
import { Button, Card } from "nhsuk-react-components"
import type { SimplifiedAppointment } from "../../../appointment.types"

import { useNavigate } from "react-router-dom"
import { getRescheduleQuestionnaireReference } from "../../../fhirUtils/healthcareServiceUtils"
import { ContactEntry, PageFunction } from "../../../types"
import "../Appointment.scss"
import { toStartCase } from "../../../utils/stringFormatters"
import { AppointmentLocationDisplay } from "./AppointmentLocationDisplay"

type Props = {
  appointment: SimplifiedAppointment
  appointmentView: boolean
  healthcareService: HealthcareService
  location?: Location
  canReschedule?: boolean
  showAppointmentStatus?: boolean
} & (
  | {
      showPageFunctionContactMessage: true
      pageFunction: PageFunction
    }
  | {
      showPageFunctionContactMessage?: false
      pageFunction?: undefined
    }
)

export function AppointmentCard({
  appointment,
  appointmentView,
  canReschedule,
  healthcareService,
  location,
  showAppointmentStatus = false,
  showPageFunctionContactMessage = false,
  pageFunction,
}: Props): JSX.Element {
  const navigate = useNavigate()

  const { canBeRescheduled, requestStatus, status, deliveryChannel, specialty, participantPractitioner } = appointment
  const statusDisplay = requestStatus != null ? `${status} (${requestStatus ?? ""})` : status

  const contactSystems = new Set((healthcareService.telecom ?? []).map((contactType) => contactType.system ?? "other"))

  const contactEntries: ContactEntry[] = Array.from(contactSystems).map((system) => {
    return {
      system,
      entries: (healthcareService.telecom ?? [])
        .filter((contactType) => contactType.system === system)
        .map((contactType) => contactType.value ?? "")
        .filter((it) => it.length > 1),
    }
  })

  const rescheduleQuestionnaireId = getRescheduleQuestionnaireReference(healthcareService).split("/")[1]

  const onAskToRescheduleAppointment = () => {
    if (rescheduleQuestionnaireId) {
      navigate(`reschedule/${rescheduleQuestionnaireId}`, { relative: "path" })
      return
    }
    navigate("reschedule", { relative: "path" })
  }

  const contactMessage = `Contact the clinic to ${pageFunction} your appointment:`
  return (
    <Card>
      <Card.Content>
        <h2>{appointment?.appointmentDate}</h2>
        <h3>{appointment?.appointmentTime}</h3>
        {showAppointmentStatus && <AppointmentDataDisplay key="status" data={statusDisplay} />}
        {appointmentView && specialty && specialty.length && (
          <AppointmentCardBlock heading="Appointment type" key="Appointment type" data={specialty[0]} />
        )}
        {deliveryChannel === "telephone" && (
          <AppointmentLocationDisplay locationName="This is a telephone appointment" />
        )}
        {deliveryChannel === "video" && <AppointmentLocationDisplay locationName="This is a video appointment" />}
        {deliveryChannel === "in-person" && <AppointmentLocationCardBlock location={location} />}
        {participantPractitioner.length >= 1 && (
          <AppointmentCardBlock heading="Clinician" key="Clinician" data={participantPractitioner} />
        )}
        {!showPageFunctionContactMessage &&
          canBeRescheduled &&
          rescheduleQuestionnaireId &&
          (canReschedule ?? false) && (
            <Button onClick={onAskToRescheduleAppointment}>Ask to reschedule appointment</Button>
          )}
        {showPageFunctionContactMessage && pageFunction && (
          <div className="contact-message">
            <strong className="contact-message__text">{contactMessage}</strong>
            <ul>
              {contactEntries.map((contactType) => {
                return (
                  <li key={contactType.system}>
                    {contactType.system}{" "}
                    {contactType.entries.map((contactEntry, index) => {
                      const linkEntry = (
                        <span className="text-link" key={contactEntry}>
                          {contactEntry}
                        </span>
                      )
                      if (contactType.entries.length === 1 || index < 1) {
                        return linkEntry
                      }

                      const joiner = index === contactType.entries.length - 1 ? " or " : ", "
                      return (
                        <span key={contactEntry}>
                          {joiner}
                          {linkEntry}
                        </span>
                      )
                    })}
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </Card.Content>
    </Card>
  )

  function AppointmentCardBlock({ heading, data }: { heading: string; data?: string | string[] }): JSX.Element | null {
    if (data == null) return null

    if (typeof data == "string") {
      return (
        <>
          <h4 style={{ marginBottom: "8px" }}>{heading}</h4>
          <AppointmentDataDisplay data={toStartCase(data)} />
        </>
      )
    }
    return (
      <>
        <h4 style={{ marginBottom: "8px" }}>{heading}</h4>
        {data.map((displayValue) => (
          <AppointmentDataDisplay data={displayValue} key={displayValue} />
        ))}
      </>
    )
  }

  function AppointmentDataDisplay({ data }: { data: string }): JSX.Element {
    return <p className="appointment-data">{toStartCase(data)}</p>
  }

  function AppointmentLocationCardBlock({ location }: { location?: Location }): JSX.Element | null {
    if (!location?.address?.line?.[0]) {
      return null
    }

    let display = location.address.line[0]

    if (location?.address?.postalCode) {
      display = `${toStartCase(display)}, ${location.address.postalCode.toUpperCase()}`
    }

    return <AppointmentLocationDisplay locationName={display} />
  }
}
